import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, ThumbsUp, ThumbsDown } from 'lucide-react';
import { Header } from './Header';
import { MusicPlayer } from './MusicPlayer';
interface AIResponseScreenProps {
  mood: string;
  onContinueChat: () => void;
  onSettingsClick: () => void;
  onFeedback: (positive: boolean) => void;
}
export function AIResponseScreen({
  mood,
  onContinueChat,
  onSettingsClick,
  onFeedback
}: AIResponseScreenProps) {
  return <div className="min-h-screen bg-gradient-to-b from-white to-sky-50 flex flex-col" data-id="element-9">
      <Header onSettingsClick={onSettingsClick} data-id="element-10" />

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="flex-1 overflow-y-auto px-6 py-6" data-id="element-11">
        <div className="max-w-md mx-auto space-y-6 pb-24" data-id="element-12">
          {/* User Mood */}
          <div className="bg-sky-100 rounded-2xl px-4 py-3 inline-block" data-id="element-13">
            <p className="text-slate-700" data-id="element-14">
              <span className="font-medium" data-id="element-15">You shared:</span> {mood}
            </p>
          </div>

          {/* Bible Verses */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100" data-id="element-16">
            <div className="flex items-center gap-2 mb-4" data-id="element-17">
              <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-400 rounded-full flex items-center justify-center" data-id="element-18">
                <span className="text-white text-sm" data-id="element-19">📖</span>
              </div>
              <h3 className="font-semibold text-slate-800" data-id="element-20">
                Scripture for You
              </h3>
            </div>

            <blockquote className="space-y-4" data-id="element-21">
              <div className="border-l-4 border-sky-400 pl-4" data-id="element-22">
                <p className="scripture-text text-slate-700 leading-relaxed mb-2" data-id="element-23">
                  "Cast all your anxiety on him because he cares for you."
                </p>
                <cite className="text-sm text-slate-500 not-italic" data-id="element-24">
                  — 1 Peter 5:7 (NIV)
                </cite>
              </div>

              <div className="border-l-4 border-purple-400 pl-4" data-id="element-25">
                <p className="scripture-text text-slate-700 leading-relaxed mb-2" data-id="element-26">
                  "Do not be anxious about anything, but in every situation, by
                  prayer and petition, with thanksgiving, present your requests
                  to God."
                </p>
                <cite className="text-sm text-slate-500 not-italic" data-id="element-27">
                  — Philippians 4:6 (NIV)
                </cite>
              </div>
            </blockquote>
          </motion.div>

          {/* Reflection */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100" data-id="element-28">
            <div className="flex items-center gap-2 mb-4" data-id="element-29">
              <div className="w-8 h-8 bg-gradient-to-br from-sky-400 to-blue-400 rounded-full flex items-center justify-center" data-id="element-30">
                <span className="text-white text-sm" data-id="element-31">💭</span>
              </div>
              <h3 className="font-semibold text-slate-800" data-id="element-32">Reflection</h3>
            </div>

            <div className="space-y-3 text-slate-700 leading-relaxed" data-id="element-33">
              <p data-id="element-34">
                When anxiety weighs heavy on your heart, remember that you are
                not alone. God invites you to bring every worry, every fear, and
                every burden to Him.
              </p>
              <p data-id="element-35">
                The act of casting your cares upon Him is not a sign of
                weakness, but a demonstration of trust. It's an acknowledgment
                that He is bigger than your circumstances and more capable than
                your own strength.
              </p>
              <p data-id="element-36">
                Today, take a moment to breathe deeply. Name each anxiety, and
                then intentionally release it to God's care. He is listening, He
                is present, and He cares deeply for you.
              </p>
              <p data-id="element-37">
                Peace is not the absence of trouble, but the presence of God in
                the midst of it. Let His peace guard your heart and mind today.
              </p>
            </div>
          </motion.div>

          {/* Prayer */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100" data-id="element-38">
            <div className="flex items-center gap-2 mb-4" data-id="element-39">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center" data-id="element-40">
                <span className="text-white text-sm" data-id="element-41">🙏</span>
              </div>
              <h3 className="font-semibold text-slate-800" data-id="element-42">A Prayer for You</h3>
            </div>

            <p className="scripture-text text-slate-700 leading-relaxed italic" data-id="element-43">
              Heavenly Father, I come before You with a heart that feels anxious
              and overwhelmed. I thank You that You invite me to cast all my
              cares upon You, because You care for me. Help me to release the
              worries I've been carrying and trust in Your perfect plan. Fill me
              with Your peace that surpasses all understanding. Guard my heart
              and mind in Christ Jesus. Remind me that You are in control, and
              that I can rest in Your loving arms. In Jesus' name, Amen.
            </p>
          </motion.div>

          {/* Music Recommendation */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} data-id="element-44">
            <div className="flex items-center gap-2 mb-3" data-id="element-45">
              <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full flex items-center justify-center" data-id="element-46">
                <span className="text-white text-sm" data-id="element-47">🎵</span>
              </div>
              <h3 className="font-semibold text-slate-800" data-id="element-48">Now Playing</h3>
            </div>

            <MusicPlayer title="Peace Be Still" artist="The Belonging Co" albumArt="https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=400&h=400&fit=crop" streamingUrl="https://open.spotify.com" data-id="element-49" />
          </motion.div>
        </div>
      </motion.div>

      {/* Feedback Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-6 py-4 shadow-lg" data-id="element-50">
        <div className="max-w-md mx-auto flex items-center justify-between gap-4" data-id="element-51">
          <div className="flex items-center gap-2" data-id="element-52">
            <span className="text-sm text-slate-600" data-id="element-53">Was this helpful?</span>
            <button onClick={() => onFeedback(true)} className="p-2 rounded-full hover:bg-green-50 text-slate-400 hover:text-green-600 transition-colors focus:outline-none focus:ring-2 focus:ring-green-400" aria-label="This was helpful" data-id="element-54">
              <ThumbsUp className="w-5 h-5" data-id="element-55" />
            </button>
            <button onClick={() => onFeedback(false)} className="p-2 rounded-full hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors focus:outline-none focus:ring-2 focus:ring-red-400" aria-label="This was not helpful" data-id="element-56">
              <ThumbsDown className="w-5 h-5" data-id="element-57" />
            </button>
          </div>

          <button onClick={onContinueChat} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-sky-400 to-purple-400 text-white font-medium rounded-full hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-purple-400" data-id="element-58">
            <MessageCircle className="w-4 h-4" data-id="element-59" />
            <span data-id="element-60">Continue</span>
          </button>
        </div>
      </div>
    </div>;
}